import {
	Component, Input, Output, OnChanges, SimpleChanges, SimpleChange, EventEmitter,
	OnInit, OnDestroy, ViewChild, ElementRef
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { IdentityService } from '../../services/identity.service';
import { NavigationService } from 'netview';
import { UsersService } from 'identity';
import { AccountType, ActiveDirectorySettingsDto, GoogleWorkspaceSettingsDto, LoginResultDto } from '../../../UvExplorerServer.Dtos.TS/Auth';

@Component({
	selector: 'login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy, OnChanges {

	public userType: string;
	public username: string;
	public password: string;
	public domainName: string;

	public userTypes: string[];
	public domainNames: string[];

	private adSettingsLoadedSub: Subscription;
	private gwSettingsLoadedSub: Subscription;

	public error = "";
	@ViewChild('signinframe', { static: false })
	public signinFrame: ElementRef<HTMLIFrameElement>;

	private static readonly LoginFailedMessage: string = "Login Failed";

	constructor(private identityService: IdentityService,
		private usersService: UsersService,
		private navService: NavigationService /*needed to cause NavigationService initialization before login*/) {

		this.initFields();

		this.adSettingsLoadedSub = this.usersService.onActiveDirectorySettingsLoaded.subscribe(
			(result: ActiveDirectorySettingsDto) => {
				this.setUserTypes();
				this.setDomainNames();
			});
		this.gwSettingsLoadedSub = this.usersService.onGoogleWorkspaceSettingsLoaded.subscribe(
			(result: GoogleWorkspaceSettingsDto) => {
				this.setUserTypes();
				this.setDomainNames();
			});
	}

	private initFields(): void {
		this.userType = "";
		this.username = "";
		this.password = "";
		this.domainName = "";

		this.setUserTypes();
		this.setDomainNames();
	}

	private setUserTypes(): void {
		this.userTypes = [];
		this.userType = "";

		if (this.usersService.hasActiveDirectorySettings) {

			let adSettings = this.usersService.adSettings;

			if (adSettings.Enabled) {
				this.userTypes.push(AccountType[AccountType.ActiveDirectory])
				if (!this.userType) {
					this.userType = AccountType[AccountType.ActiveDirectory];
				}
			}
		}

		if (this.usersService.hasGoogleWorkspaceSettings) {

			let gwSettings = this.usersService.gwSettings;

			if (gwSettings.Enabled) {
				this.userTypes.push(AccountType[AccountType.GoogleWorkspace]);
				if (!this.userType) {
					this.userType = AccountType[AccountType.GoogleWorkspace];
				}
			}
		}

		if (this.usersService.hasSamlSettings) {

			let samlSettings = this.usersService.samlSettings;

			if (samlSettings.Enabled) {
				this.userTypes.push(AccountType[AccountType.Saml]);
				if (!this.userType) {
					this.userType = AccountType[AccountType.Saml];
				}
			}
		}

		this.userTypes.push(AccountType[AccountType.Native]);
		if (!this.userType) {
			this.userType = AccountType[AccountType.Native];
		}

		this.onUserTypeChanged();
	}

	private setDomainNames(): void {
		this.domainNames = [];

		switch (this.userType) {
			case AccountType[AccountType.Native]:
			case AccountType[AccountType.Saml]:
				// No domains
				break;
			case AccountType[AccountType.ActiveDirectory]:
				if (this.usersService.hasActiveDirectorySettings) {
					this.domainNames = this.usersService.adSettings.DomainNames.ToArray();
				}
				break;
			case AccountType[AccountType.GoogleWorkspace]:
				if (this.usersService.hasGoogleWorkspaceSettings) {
					this.domainNames = this.usersService.gwSettings.DomainNames.ToArray();
				}
				break;
			default:
				console.assert(false, "Invalid user type");
				break;
		}

		if (this.domainNames.length > 0) {
			this.domainName = this.domainNames[0];
		}
		else {
			this.domainName = "";
		}
	}

	public authTypeLabel(value: string): string {
		switch (value) {
			case AccountType[AccountType.Native]:
				return "UVexplorer";
			case AccountType[AccountType.ActiveDirectory]:
				return "Active Directory";
			case AccountType[AccountType.GoogleWorkspace]:
				return "Google Workspace";
			case AccountType[AccountType.Saml]:
				return "SAML Single Sign-On";
			default:
				return "";
		}
	}

	public ngOnInit(): void {
		this.initFields();
	}

	public ngAfterViewInit() {
		if (this.navService.location.hasContext) {
			const LoginFailedPath = '/failed';

			let context = this.navService.location.context;
			if (context === LoginFailedPath) {
				this.error = LoginComponent.LoginFailedMessage;
			}
		}
	}

	public ngOnDestroy(): void {
		if (this.adSettingsLoadedSub) {
			this.adSettingsLoadedSub.unsubscribe();
			this.adSettingsLoadedSub = null;
		}
		if (this.gwSettingsLoadedSub) {
			this.gwSettingsLoadedSub.unsubscribe();
			this.gwSettingsLoadedSub = null;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.initFields();
	}

	public onUserTypeChanged(): void {
		this.setDomainNames();

		if (this.userType === AccountType[AccountType.GoogleWorkspace]) {
			this.identityService.loginGoogleWorkspaceUser(this.finalRedirectUrl, this.errorRedirectUrl).subscribe(
				(result: LoginResultDto) => {
					if (this.signinFrame && result.HasRedirectUrl) {
						this.signinFrame.nativeElement.src = result.RedirectUrl;
					};
				});
		}
	}

	private get finalRedirectUrl(): string {

		let navDestination = "/";
		if (this.navService.navDestination) {
			navDestination = this.navService.navDestination;
		}

		return location.origin + navDestination;
	}

	private get errorRedirectUrl(): string {
		return location.origin + "/login";
	}

	public get showContent(): boolean {
		return !this.identityService.isLoggedIn;
	}

	public get enableLogin(): boolean {
		switch (this.userType) {
			case AccountType[AccountType.Native]:
				return (!!this.username && !!this.password);
			case AccountType[AccountType.ActiveDirectory]:
				return (!!this.username && !!this.password && !!this.domainName);
			case AccountType[AccountType.GoogleWorkspace]:
				return false;
			case AccountType[AccountType.Saml]:
				return true;
			default:
				console.assert(false, "Invalid user type");
				return false;
		}
	}

	public get displayUserType(): boolean {
		return (this.userTypes.length > 1);
	}

	public get displayUsername(): boolean {
		return (this.userType == AccountType[AccountType.Native] ||
			this.userType == AccountType[AccountType.ActiveDirectory]);
	}

	public get displayPassword(): boolean {
		return (this.userType == AccountType[AccountType.Native] ||
			this.userType == AccountType[AccountType.ActiveDirectory]);
	}

	public get displayDomainName(): boolean {
		return (this.userType == AccountType[AccountType.ActiveDirectory]);
	}

	public get displayLoginButton(): boolean {
		return (this.userType == AccountType[AccountType.Native] ||
			this.userType == AccountType[AccountType.ActiveDirectory] ||
			this.userType == AccountType[AccountType.Saml]);
	}

	public get displayGoogleWorkspaceLoginButton(): boolean {
		return (this.userType == AccountType[AccountType.GoogleWorkspace]);
	}

	public login(): void {
		switch (this.userType) {
			case AccountType[AccountType.Native]:
				console.log("Type is native")
				this.identityService.loginNativeUser(this.username, this.password).subscribe(
					data => { },
					error => this.error = LoginComponent.LoginFailedMessage
				);
				break;
			case AccountType[AccountType.ActiveDirectory]:
				this.identityService.loginActiveDirectoryUser(this.username, this.password, this.domainName).subscribe(
					data => { },
					error => this.error = LoginComponent.LoginFailedMessage
				);
				break;
			//case AccountType[AccountType.GoogleWorkspace]:
			//	this.identityService.loginGoogleWorkspaceUser(this.finalRedirectUrl, this.errorRedirectUrl);
			//	break;
			case AccountType[AccountType.Saml]:
				this.identityService.loginSamlUser(this.finalRedirectUrl, this.errorRedirectUrl).subscribe(
					data => { },
					error => this.error = LoginComponent.LoginFailedMessage
				);
				break;
			default:
				console.assert(false, "Invalid user type");
				break;
		}

		this.password = "";
	}

	public loginOnEnter(event: any): void {
		if (this.enableLogin) {
			if (event.keyCode === 13) {
				this.login();
			}
		}
	}

}
